import { loadTaxonomy, refreshTaxonomy } from '@/lib/taxonomy-loader';
import { TagPreference } from '@/components/drill-library/types/drill-types';

// Cache for loaded colors with timestamp
let cachedColors: { categoryLevelColors: any; optionColors: any } | null = null;
let colorsTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function loadColors() {
  const now = Date.now();

  // Return cached colors if still valid
  if (cachedColors && now - colorsTimestamp < CACHE_DURATION) {
    return cachedColors;
  }

  try {
    const taxonomy = await loadTaxonomy();
    if (taxonomy.colors) {
      cachedColors = taxonomy.colors;
      colorsTimestamp = now;
      return cachedColors;
    }
  } catch (e) {
    logger.error('Failed to load colors from taxonomy', e);
  }

  // Fallback to empty colors (no defaults)
  cachedColors = {
    categoryLevelColors: {},
    optionColors: {}
  };
  colorsTimestamp = now;
  return cachedColors;
}
import logger from '@/lib/logging';

/**
 * Refresh color cache (call after tag system changes)
 */
export async function refreshColors() {
  cachedColors = null;
  colorsTimestamp = 0;
  await refreshTaxonomy();
  return loadColors();
}

/**
 * Get color for a tag option
 * @param category - Tag category name
 * @param optionValue - Tag option value
 * @param tagPreferences - Legacy tag preferences (optional)
 * @returns Hex color string
 */
export function getTagColor(category: string, optionValue: string, tagPreferences: { [key: string]: TagPreference } = {}): string {
  const prefKey = `${category}::${optionValue}`;
  
  // Check tag preferences first (legacy support)
  if (tagPreferences[prefKey]?.color) {
    return tagPreferences[prefKey].color;
  }
  
  // Check option-specific color override
  if (cachedColors?.optionColors && cachedColors.optionColors[optionValue]) {
    return cachedColors.optionColors[optionValue];
  }
  
  // Inherit from category-level color
  if (cachedColors?.categoryLevelColors && cachedColors.categoryLevelColors[category]) {
    return cachedColors.categoryLevelColors[category];
  }
  
  // Fallback to static defaults
  return cachedColors?.optionColors?.[optionValue] || '#64748B';
}

/**
 * Legacy function for backward compatibility
 */
export function getCategoryColor(category: string, tagPreferences: { [key: string]: TagPreference } = {}): string {
  return getTagColor('Domain', category, tagPreferences);
}

/**
 * Get tooltip text for tags containing IR/ER abbreviations.
 * IR = Internal Rotation, ER = External Rotation.
 * @param value - Tag option value (e.g. "Very Limited Anterior Rib Cage Expansion (IR)")
 * @returns Tooltip string if value contains IR/ER, otherwise undefined
 */
export function getTagTooltip(value: string): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  const parts: string[] = [];
  // Match (IR), (IR++), or standalone IR
  if (/\(IR(?:\+\+)?\)|\bIR\b/.test(value)) {
    parts.push('IR = Internal Rotation');
  }
  // Match (ER), (ER++), or standalone ER (avoid matching "Posterior", "Propulsion" etc.)
  if (/\(ER(?:\+\+)?\)|\bER\b/.test(value)) {
    parts.push('ER = External Rotation');
  }
  return parts.length > 0 ? parts.join('; ') : undefined;
}

export function colorIsLight(hex: string): boolean {
  if (!hex) return false;
  try {
    let c = hex.substring(1); // strip #
    if (c.length === 3) {
      c = c.split('').map(char => char + char).join('');
    }
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    // HSP equation from http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(
      0.299 * (r * r) +
      0.587 * (g * g) +
      0.114 * (b * b)
    );
    return hsp > 127.5;
  } catch(e) {
    return false;
  }
}

export function getYoutubeVideoId(url: string | undefined): string | null {
  if (!url || typeof url !== 'string' || url.trim().length === 0) return null;
  
  // Basic validation: URL should start with http:// or https://
  const trimmedUrl = url.trim();
  if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
    return null;
  }
  
  let videoId = null;
  try {
    const urlObj = new URL(trimmedUrl);
    if (urlObj.hostname === 'youtu.be') {
      videoId = urlObj.pathname.slice(1);
    } else if (urlObj.hostname.includes('youtube.com')) {
      videoId = urlObj.searchParams.get('v');
    }
  } catch (e) {
    // Silently handle invalid URLs - don't log to console
    return null;
  }
  return videoId;
}

// Video embed utilities
export function getEmbedUrl(videoUrl: string): { src: string; type: 'iframe' | 'video' } | null {
  if (!videoUrl) return null;
  
  const videoId = getYoutubeVideoId(videoUrl);
  if (videoId) {
    return {
      src: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
      type: 'iframe'
    };
  }
  
  // Handle direct video files
  if (videoUrl.match(/\.(mp4|webm|ogg)$/i)) {
    return {
      src: videoUrl,
      type: 'video'
    };
  }
  
  return null;
}
